// src/cockpit4/components/CompetitorMonitor.tsx
// ============================================
// P1-功能：竞品监控与分析（营销中心）
//
// 功能：
//   1. 监控竞品账号的内容和数据（用户手动添加竞品 + 手动录入内容数据）
//   2. 竞品内容策略分析（选题方向 / 内容形式 / 发布频率 / 发布时段）
//   3. 竞品爆款拆解（用户录入的爆款内容卡片，含数据与拆解分析）
//   4. 差异化机会识别（基于竞品内容分析与我方 marketing 数据的对比）
//   5. 竞品动态告警（用户设置告警规则，竞品发布爆款/改变策略时触发告警）
//
// 数据来源：
//   - localStorage（key: xeaos_marketing_competitors）持久化竞品列表（用户手动添加）
//   - localStorage（key: xeaos_marketing_competitor_content_<competitorId>）持久化每个竞品的内容数据
//   - localStorage（key: xeaos_marketing_competitor_alerts）持久化告警事件
//   - useDashboardData 的 marketing 数据（用于与我方数据对比，可选）
//   - 没有用户添加的竞品时显示空状态提示，绝不展示假竞品数据
//   - 竞品分析维度为内置分析框架（选题/频率/表现/爆款率/形式/时段），非假数据
// ============================================

import { useCallback, useEffect, useMemo, useState } from "react";
import { motion, AnimatePresence } from "framer-motion";
import {
  Eye,
  Plus,
  X,
  TrendingUp,
  Award,
  AlertCircle,
  BarChart3,
  Crosshair,
  RefreshCw,
  Loader2,
  Trash2,
  ExternalLink,
  ChevronDown,
  ChevronRight,
  Bell,
  Edit2,
  Clock,
  FileText,
  Lightbulb,
} from "lucide-react";
import { useDashboardData } from "../../hooks/useDashboardData";
import type { MarketingPlatform, PlatformMetrics } from "../../hooks/useDashboardData";

// ============================================
// 类型与常量
// ============================================

const COMPETITORS_STORAGE_KEY = "xeaos_marketing_competitors";
const CONTENT_STORAGE_PREFIX = "xeaos_marketing_competitor_content_";
const ALERTS_STORAGE_KEY = "xeaos_marketing_competitor_alerts";

/** 内容选题方向（分析框架维度，非假数据） */
type ContentTopic =
  | "tech"
  | "business"
  | "education"
  | "entertainment"
  | "lifestyle"
  | "finance"
  | "industry"
  | "other";

/** 内容形式 */
type ContentForm = "image_text" | "video" | "text";

/** 告警规则类型 */
type AlertRuleType = "hit_published" | "strategy_changed" | "high_frequency" | "frequency_drop";

/** 竞品账号 */
interface Competitor {
  id: string;
  /** 账号名 */
  name: string;
  /** 平台 */
  platform: MarketingPlatform;
  /** 账号链接（选填） */
  link?: string;
  /** 备注（选填） */
  note?: string;
  /** 创建日期 */
  createdAt: string;
  /** 监控开关 */
  monitoring: boolean;
  /** 告警规则（内嵌） */
  alertRules: AlertRule[];
}

/** 竞品内容记录（用户手动录入） */
interface CompetitorContent {
  id: string;
  competitorId: string;
  /** 标题 */
  title: string;
  /** 发布日期 YYYY-MM-DD */
  publishedAt: string;
  /** 阅读/播放量 */
  views: number;
  /** 点赞数 */
  likes: number;
  /** 评论数 */
  comments: number;
  /** 内容形式 */
  form: ContentForm;
  /** 选题方向 */
  topic: ContentTopic;
  /** 是否爆款（用户标记或自动判定） */
  isHit: boolean;
  /** 爆款拆解分析（用户录入） */
  breakdown?: string;
  /** 录入时间 */
  createdAt: string;
}

/** 告警规则 */
interface AlertRule {
  id: string;
  type: AlertRuleType;
  enabled: boolean;
  createdAt: string;
}

/** 告警事件 */
interface AlertEvent {
  id: string;
  competitorId: string;
  competitorName: string;
  type: AlertRuleType;
  message: string;
  /** 触发时间 YYYY-MM-DD HH:mm */
  triggeredAt: string;
  read: boolean;
}

// ============================================
// 元数据映射（内置分析框架，非假数据）
// ============================================

const PLATFORM_META: Record<MarketingPlatform, { label: string; color: string; viewsLabel: string }> = {
  wechat_official: { label: "公众号", color: "#10B981", viewsLabel: "阅读量" },
  xiaohongshu: { label: "小红书", color: "#EF4444", viewsLabel: "曝光量" },
  douyin_enterprise: { label: "抖音企业号", color: "#06B6D4", viewsLabel: "播放量" },
  douyin_personal: { label: "抖音个人号", color: "#8B5CF6", viewsLabel: "播放量" },
  zhihu: { label: "知乎", color: "#3B82F6", viewsLabel: "浏览量" },
  bilibili: { label: "B站", color: "#FB7299", viewsLabel: "播放量" },
};

const TOPIC_META: Record<ContentTopic, { label: string; color: string }> = {
  tech: { label: "科技", color: "#3B82F6" },
  business: { label: "商业", color: "#10B981" },
  education: { label: "教育", color: "#60A5FA" },
  entertainment: { label: "娱乐", color: "#EC4899" },
  lifestyle: { label: "生活", color: "#F59E0B" },
  finance: { label: "财经", color: "#EAB308" },
  industry: { label: "行业", color: "#A855F7" },
  other: { label: "其他", color: "#6B7280" },
};

const FORM_META: Record<ContentForm, { label: string }> = {
  image_text: { label: "图文" },
  video: { label: "视频" },
  text: { label: "纯文" },
};

const ALERT_RULE_META: Record<AlertRuleType, { label: string; desc: string }> = {
  hit_published: { label: "爆款发布", desc: "竞品发布爆款内容时告警" },
  strategy_changed: { label: "策略变化", desc: "竞品选题/形式方向发生偏移时告警" },
  high_frequency: { label: "高频发布", desc: "竞品发布频率异常升高时告警" },
  frequency_drop: { label: "频率下降", desc: "竞品发布频率异常下降时告警" },
};

/** 平台选项列表（用于下拉选择） */
const PLATFORM_OPTIONS: MarketingPlatform[] = [
  "wechat_official",
  "xiaohongshu",
  "douyin_enterprise",
  "douyin_personal",
  "zhihu",
  "bilibili",
];

/** 选题方向选项列表 */
const TOPIC_OPTIONS: ContentTopic[] = [
  "tech",
  "business",
  "education",
  "entertainment",
  "lifestyle",
  "finance",
  "industry",
  "other",
];

/** 内容形式选项列表 */
const FORM_OPTIONS: ContentForm[] = ["image_text", "video", "text"];

/** 告警规则选项列表 */
const ALERT_RULE_OPTIONS: AlertRuleType[] = [
  "hit_published",
  "strategy_changed",
  "high_frequency",
  "frequency_drop",
];

// ============================================
// 工具函数
// ============================================

/** 生成唯一 ID */
function genId(prefix: string): string {
  return `${prefix}_${Date.now()}_${Math.random().toString(36).slice(2, 8)}`;
}

/** YYYY-MM-DD */
function todayStr(): string {
  return new Date().toISOString().slice(0, 10);
}

/** YYYY-MM-DD HH:mm */
function nowStr(): string {
  const d = new Date();
  const date = d.toISOString().slice(0, 10);
  const hh = String(d.getHours()).padStart(2, "0");
  const mm = String(d.getMinutes()).padStart(2, "0");
  return `${date} ${hh}:${mm}`;
}

/** 从日期字符串提取小时（用于时段分布分析） */
function hourFromDate(dateStr: string): number {
  try {
    return new Date(dateStr).getHours();
  } catch {
    return 12;
  }
}

/** 安全读取 localStorage JSON */
function loadJSON<T>(key: string, fallback: T): T {
  try {
    const raw = localStorage.getItem(key);
    if (!raw) return fallback;
    const parsed = JSON.parse(raw);
    return parsed as T;
  } catch {
    return fallback;
  }
}

/** 安全写入 localStorage JSON */
function saveJSON<T>(key: string, value: T): void {
  try {
    localStorage.setItem(key, JSON.stringify(value));
  } catch {
    // 容量超限或隐私模式：忽略
  }
}

/** 加载竞品列表 */
function loadCompetitors(): Competitor[] {
  const list = loadJSON<unknown[]>(COMPETITORS_STORAGE_KEY, []);
  return Array.isArray(list) ? (list as Competitor[]) : [];
}

/** 加载某竞品的内容数据 */
function loadContents(competitorId: string): CompetitorContent[] {
  const list = loadJSON<unknown[]>(`${CONTENT_STORAGE_PREFIX}${competitorId}`, []);
  return Array.isArray(list) ? (list as CompetitorContent[]) : [];
}

/** 加载告警事件 */
function loadAlerts(): AlertEvent[] {
  const list = loadJSON<unknown[]>(ALERTS_STORAGE_KEY, []);
  return Array.isArray(list) ? (list as AlertEvent[]) : [];
}

// ============================================
// 分析框架（内置分析维度，基于用户录入的竞品内容计算）
// ============================================

/** 竞品内容策略分析结果 */
interface ContentStrategy {
  /** 内容总数 */
  total: number;
  /** 选题方向分布 */
  topicDistribution: { topic: ContentTopic; count: number; pct: number }[];
  /** 内容形式分布 */
  formDistribution: { form: ContentForm; count: number; pct: number }[];
  /** 发布频率：日均（条/天） */
  dailyFrequency: number;
  /** 发布频率：周均（条/周） */
  weeklyFrequency: number;
  /** 平均阅读/播放量 */
  avgViews: number;
  /** 平均点赞数 */
  avgLikes: number;
  /** 平均评论数 */
  avgComments: number;
  /** 爆款率（爆款内容占比 %） */
  hitRate: number;
  /** 爆款数量 */
  hitCount: number;
  /** 发布时段分布（0-23 小时区间统计） */
  hourDistribution: { hour: number; count: number }[];
  /** 主力选题方向（占比最高的） */
  primaryTopic: ContentTopic | null;
  /** 主力内容形式（占比最高的） */
  primaryForm: ContentForm | null;
  /** 活跃时段（发布最多的时段） */
  peakHour: number | null;
  /** 时间跨度天数 */
  spanDays: number;
}

/** 计算竞品内容策略分析 */
function analyzeContentStrategy(contents: CompetitorContent[]): ContentStrategy {
  const total = contents.length;
  if (total === 0) {
    return {
      total: 0,
      topicDistribution: [],
      formDistribution: [],
      dailyFrequency: 0,
      weeklyFrequency: 0,
      avgViews: 0,
      avgLikes: 0,
      avgComments: 0,
      hitRate: 0,
      hitCount: 0,
      hourDistribution: [],
      primaryTopic: null,
      primaryForm: null,
      peakHour: null,
      spanDays: 0,
    };
  }

  // 选题方向分布
  const topicCounter = new Map<ContentTopic, number>();
  for (const c of contents) {
    topicCounter.set(c.topic, (topicCounter.get(c.topic) || 0) + 1);
  }
  const topicDistribution = Array.from(topicCounter.entries())
    .map(([topic, count]) => ({ topic, count, pct: Math.round((count / total) * 100) }))
    .sort((a, b) => b.count - a.count);

  // 内容形式分布
  const formCounter = new Map<ContentForm, number>();
  for (const c of contents) {
    formCounter.set(c.form, (formCounter.get(c.form) || 0) + 1);
  }
  const formDistribution = Array.from(formCounter.entries())
    .map(([form, count]) => ({ form, count, pct: Math.round((count / total) * 100) }))
    .sort((a, b) => b.count - a.count);

  // 平均表现
  const sumViews = contents.reduce((s, c) => s + (c.views || 0), 0);
  const sumLikes = contents.reduce((s, c) => s + (c.likes || 0), 0);
  const sumComments = contents.reduce((s, c) => s + (c.comments || 0), 0);

  // 爆款
  const hitContents = contents.filter((c) => c.isHit);
  const hitCount = hitContents.length;

  // 时段分布
  const hourCounter = new Map<number, number>();
  for (let h = 0; h < 24; h++) hourCounter.set(h, 0);
  for (const c of contents) {
    const h = hourFromDate(c.publishedAt);
    hourCounter.set(h, (hourCounter.get(h) || 0) + 1);
  }
  const hourDistribution = Array.from(hourCounter.entries())
    .map(([hour, count]) => ({ hour, count }))
    .sort((a, b) => a.hour - b.hour);

  // 时间跨度
  const dates = contents
    .map((c) => new Date(c.publishedAt).getTime())
    .filter((t) => !isNaN(t));
  const minDate = dates.length > 0 ? Math.min(...dates) : 0;
  const maxDate = dates.length > 0 ? Math.max(...dates) : 0;
  const spanDays = dates.length > 0 ? Math.max(1, Math.round((maxDate - minDate) / 86400000)) : 0;

  // 频率
  const dailyFrequency = spanDays > 0 ? Math.round((total / spanDays) * 100) / 100 : 0;
  const weeklyFrequency = spanDays > 0 ? Math.round((total / spanDays) * 7 * 100) / 100 : 0;

  return {
    total,
    topicDistribution,
    formDistribution,
    dailyFrequency,
    weeklyFrequency,
    avgViews: Math.round(sumViews / total),
    avgLikes: Math.round(sumLikes / total),
    avgComments: Math.round(sumComments / total),
    hitRate: Math.round((hitCount / total) * 100),
    hitCount,
    hourDistribution,
    primaryTopic: topicDistribution.length > 0 ? topicDistribution[0].topic : null,
    primaryForm: formDistribution.length > 0 ? formDistribution[0].form : null,
    peakHour: hourDistribution.length > 0
      ? hourDistribution.reduce((max, cur) => (cur.count > max.count ? cur : max)).hour
      : null,
    spanDays,
  };
}

/** 我方平台数据统计（从 useDashboardData 的 marketing 数据提取） */
interface MyPlatformStats {
  platform: MarketingPlatform;
  metrics: PlatformMetrics | null;
  available: boolean;
}

/** 提取我方平台数据 */
function extractMyStats(
  platforms: Record<MarketingPlatform, { metrics: PlatformMetrics; degraded: boolean }>,
  platform: MarketingPlatform
): MyPlatformStats {
  const data = platforms[platform];
  if (!data || data.degraded) {
    return { platform, metrics: null, available: false };
  }
  return { platform, metrics: data.metrics, available: true };
}

/** 差异化机会分析结果 */
interface Opportunity {
  id: string;
  type: "topic_gap" | "performance_gap" | "form_gap" | "hit_pattern";
  title: string;
  detail: string;
  /** 影响等级 */
  level: "high" | "medium" | "low";
}

/**
 * 差异化机会识别
 * 基于竞品内容策略与我方平台数据的对比，生成可执行的差异化建议
 */
function identifyOpportunities(
  strategy: ContentStrategy,
  myStats: MyPlatformStats
): Opportunity[] {
  const ops: Opportunity[] = [];
  if (strategy.total === 0) return ops;

  // 1. 选题方向机会：识别竞品主力选题与空白选题
  if (strategy.primaryTopic) {
    const primary = TOPIC_META[strategy.primaryTopic];
    ops.push({
      id: "topic_primary",
      type: "topic_gap",
      title: `竞品主力选题：${primary.label}`,
      detail: `竞品在「${primary.label}」方向占比 ${strategy.topicDistribution[0].pct}%，建议关注该方向的受众需求与内容机会。`,
      level: "high",
    });
  }
  // 竞品覆盖较少的选题方向 = 潜在差异化切入点
  const lowTopics = strategy.topicDistribution.filter((t) => t.pct <= 10);
  if (lowTopics.length > 0) {
    const labels = lowTopics.map((t) => TOPIC_META[t.topic].label).join("、");
    ops.push({
      id: "topic_low",
      type: "topic_gap",
      title: `竞品薄弱选题：${labels}`,
      detail: `这些方向竞品覆盖较少（占比 <=10%），是我方差异化切入的机会点。`,
      level: "medium",
    });
  }

  // 2. 表现差距：我方 vs 竞品
  if (myStats.available && myStats.metrics) {
    const myViews = myStats.metrics.views;
    const compViews = strategy.avgViews;
    if (compViews > 0) {
      const diff = Math.round(((myViews - compViews) / compViews) * 100);
      if (diff < -20) {
        ops.push({
          id: "perf_views",
          type: "performance_gap",
          title: `阅读量落后竞品 ${Math.abs(diff)}%`,
          detail: `我方阅读量 ${myViews} vs 竞品均值 ${compViews}，差距明显，需优化标题与选题吸引力。`,
          level: "high",
        });
      } else if (diff > 20) {
        ops.push({
          id: "perf_views",
          type: "performance_gap",
          title: `阅读量领先竞品 ${diff}%`,
          detail: `我方阅读量 ${myViews} vs 竞品均值 ${compViews}，优势明显，可加大该方向投入。`,
          level: "medium",
        });
      }
    }
    const myLikes = myStats.metrics.likes;
    const compLikes = strategy.avgLikes;
    if (compLikes > 0) {
      const diff = Math.round(((myLikes - compLikes) / compLikes) * 100);
      if (diff < -30) {
        ops.push({
          id: "perf_likes",
          type: "performance_gap",
          title: `点赞数落后竞品 ${Math.abs(diff)}%`,
          detail: `我方点赞 ${myLikes} vs 竞品均值 ${compLikes}，互动引导有待加强。`,
          level: "medium",
        });
      }
    }
  }

  // 3. 内容形式机会
  if (strategy.primaryForm) {
    const primary = FORM_META[strategy.primaryForm];
    ops.push({
      id: "form_primary",
      type: "form_gap",
      title: `竞品主力形式：${primary.label}`,
      detail: `竞品以「${primary.label}」为主（占比 ${strategy.formDistribution[0].pct}%），我方可评估是否在相同或差异化形式上发力。`,
      level: "medium",
    });
  }

  // 4. 爆款特征
  if (strategy.hitCount > 0 && strategy.hitRate > 0) {
    ops.push({
      id: "hit_pattern",
      type: "hit_pattern",
      title: `爆款率 ${strategy.hitRate}%（${strategy.hitCount} 条爆款）`,
      detail: `竞品爆款集中在「${strategy.primaryTopic ? TOPIC_META[strategy.primaryTopic].label : "未知"}」方向，建议拆解其爆款标题与结构，提炼可复用公式。`,
      level: strategy.hitRate >= 30 ? "high" : "medium",
    });
  }

  // 5. 发布时段机会
  if (strategy.peakHour !== null) {
    const h = strategy.peakHour;
    ops.push({
      id: "peak_hour",
      type: "form_gap",
      title: `竞品主力发布时段：${h}:00`,
      detail: `竞品在 ${h}:00 时段发布最频繁，建议评估该时段的流量红利与我方发布策略的匹配度。`,
      level: "low",
    });
  }

  return ops;
}

// ============================================
// 主组件
// ============================================

export default function CompetitorMonitor() {
  const dashboard = useDashboardData();

  // 竞品列表
  const [competitors, setCompetitors] = useState<Competitor[]>([]);
  // 每个竞品的内容数据（按 competitorId 索引）
  const [contentsMap, setContentsMap] = useState<Record<string, CompetitorContent[]>>({});
  // 告警事件
  const [alerts, setAlerts] = useState<AlertEvent[]>([]);
  // 加载状态
  const [loading, setLoading] = useState(true);
  // 选中的竞品 ID（展开详情面板）
  const [selectedId, setSelectedId] = useState<string | null>(null);
  // 显示添加/编辑竞品 Modal
  const [showCompetitorForm, setShowCompetitorForm] = useState(false);
  // 编辑中的竞品 ID（null 表示新增）
  const [editingId, setEditingId] = useState<string | null>(null);
  // 显示录入内容 Modal
  const [showContentForm, setShowContentForm] = useState(false);
  // 录入内容的目标竞品 ID
  const [contentTargetId, setContentTargetId] = useState<string | null>(null);
  // 告警面板展开
  const [showAlertPanel, setShowAlertPanel] = useState(false);

  // 添加/编辑竞品表单
  const [competitorForm, setCompetitorForm] = useState({
    name: "",
    platform: "wechat_official" as MarketingPlatform,
    link: "",
    note: "",
  });

  // 录入内容表单
  const [contentForm, setContentForm] = useState({
    title: "",
    publishedAt: todayStr(),
    views: 0,
    likes: 0,
    comments: 0,
    form: "image_text" as ContentForm,
    topic: "tech" as ContentTopic,
    isHit: false,
    breakdown: "",
  });

  // 初次挂载：加载 localStorage 数据
  useEffect(() => {
    const comps = loadCompetitors();
    setCompetitors(comps);
    // 加载每个竞品的内容
    const map: Record<string, CompetitorContent[]> = {};
    for (const c of comps) {
      map[c.id] = loadContents(c.id);
    }
    setContentsMap(map);
    setAlerts(loadAlerts());
    setLoading(false);
  }, []);

  // 竞品列表变化时持久化
  useEffect(() => {
    saveJSON(COMPETITORS_STORAGE_KEY, competitors);
  }, [competitors]);

  // 告警事件变化时持久化
  useEffect(() => {
    saveJSON(ALERTS_STORAGE_KEY, alerts);
  }, [alerts]);

  // ============================================
  // 竞品 CRUD 操作
  // ============================================

  /** 打开添加竞品表单 */
  const openAddCompetitor = useCallback(() => {
    setEditingId(null);
    setCompetitorForm({ name: "", platform: "wechat_official", link: "", note: "" });
    setShowCompetitorForm(true);
  }, []);

  /** 打开编辑竞品表单 */
  const openEditCompetitor = useCallback((comp: Competitor) => {
    setEditingId(comp.id);
    setCompetitorForm({
      name: comp.name,
      platform: comp.platform,
      link: comp.link || "",
      note: comp.note || "",
    });
    setShowCompetitorForm(true);
  }, []);

  /** 提交竞品表单（新增或编辑） */
  const submitCompetitor = useCallback(() => {
    if (!competitorForm.name.trim()) return;
    if (editingId) {
      // 编辑
      setCompetitors((prev) =>
        prev.map((c) =>
          c.id === editingId
            ? {
                ...c,
                name: competitorForm.name.trim(),
                platform: competitorForm.platform,
                link: competitorForm.link.trim() || undefined,
                note: competitorForm.note.trim() || undefined,
              }
            : c
        )
      );
    } else {
      // 新增
      const comp: Competitor = {
        id: genId("comp"),
        name: competitorForm.name.trim(),
        platform: competitorForm.platform,
        link: competitorForm.link.trim() || undefined,
        note: competitorForm.note.trim() || undefined,
        createdAt: todayStr(),
        monitoring: true,
        alertRules: [
          { id: genId("rule"), type: "hit_published", enabled: true, createdAt: todayStr() },
        ],
      };
      setCompetitors((prev) => [...prev, comp]);
      setContentsMap((prev) => ({ ...prev, [comp.id]: [] }));
    }
    setShowCompetitorForm(false);
    setEditingId(null);
  }, [competitorForm, editingId]);

  /** 删除竞品 */
  const removeCompetitor = useCallback((id: string) => {
    setCompetitors((prev) => prev.filter((c) => c.id !== id));
    setContentsMap((prev) => {
      const next = { ...prev };
      delete next[id];
      return next;
    });
    // 清理 localStorage 中的内容数据
    try {
      localStorage.removeItem(`${CONTENT_STORAGE_PREFIX}${id}`);
    } catch {
      // 忽略
    }
    if (selectedId === id) setSelectedId(null);
  }, [selectedId]);

  /** 切换监控状态 */
  const toggleMonitoring = useCallback((id: string) => {
    setCompetitors((prev) =>
      prev.map((c) => (c.id === id ? { ...c, monitoring: !c.monitoring } : c))
    );
  }, []);

  /** 切换告警规则启用状态 */
  const toggleAlertRule = useCallback((competitorId: string, ruleId: string) => {
    setCompetitors((prev) =>
      prev.map((c) =>
        c.id === competitorId
          ? {
              ...c,
              alertRules: c.alertRules.map((r) =>
                r.id === ruleId ? { ...r, enabled: !r.enabled } : r
              ),
            }
          : c
      )
    );
  }, []);

  /** 添加告警规则 */
  const addAlertRule = useCallback((competitorId: string, type: AlertRuleType) => {
    setCompetitors((prev) =>
      prev.map((c) =>
        c.id === competitorId
          ? {
              ...c,
              alertRules: [
                ...c.alertRules,
                { id: genId("rule"), type, enabled: true, createdAt: todayStr() },
              ],
            }
          : c
      )
    );
  }, []);

  /** 删除告警规则 */
  const removeAlertRule = useCallback((competitorId: string, ruleId: string) => {
    setCompetitors((prev) =>
      prev.map((c) =>
        c.id === competitorId
          ? { ...c, alertRules: c.alertRules.filter((r) => r.id !== ruleId) }
          : c
      )
    );
  }, []);

  // ============================================
  // 内容录入操作
  // ============================================

  /** 打开录入内容表单 */
  const openContentForm = useCallback((competitorId: string) => {
    setContentTargetId(competitorId);
    setContentForm({
      title: "",
      publishedAt: todayStr(),
      views: 0,
      likes: 0,
      comments: 0,
      form: "image_text",
      topic: "tech",
      isHit: false,
      breakdown: "",
    });
    setShowContentForm(true);
  }, []);

  /** 提交内容录入 */
  const submitContent = useCallback(() => {
    if (!contentTargetId || !contentForm.title.trim()) return;
    const content: CompetitorContent = {
      id: genId("content"),
      competitorId: contentTargetId,
      title: contentForm.title.trim(),
      publishedAt: contentForm.publishedAt,
      views: contentForm.views,
      likes: contentForm.likes,
      comments: contentForm.comments,
      form: contentForm.form,
      topic: contentForm.topic,
      isHit: contentForm.isHit,
      breakdown: contentForm.breakdown.trim() || undefined,
      createdAt: nowStr(),
    };
    setContentsMap((prev) => ({
      ...prev,
      [contentTargetId]: [content, ...(prev[contentTargetId] || [])],
    }));
    // 持久化到独立 localStorage key
    const existing = loadContents(contentTargetId);
    saveJSON(`${CONTENT_STORAGE_PREFIX}${contentTargetId}`, [content, ...existing]);

    // 如果是爆款且竞品开启了爆款告警规则，则触发告警
    if (content.isHit) {
      const comp = competitors.find((c) => c.id === contentTargetId);
      if (comp) {
        const hasHitRule = comp.alertRules.some(
          (r) => r.type === "hit_published" && r.enabled
        );
        if (hasHitRule) {
          const alert: AlertEvent = {
            id: genId("alert"),
            competitorId: comp.id,
            competitorName: comp.name,
            type: "hit_published",
            message: `竞品「${comp.name}」发布爆款内容：「${content.title}」（阅读 ${content.views}）`,
            triggeredAt: nowStr(),
            read: false,
          };
          setAlerts((prev) => [alert, ...prev]);
        }
      }
    }

    setShowContentForm(false);
    setContentTargetId(null);
  }, [contentForm, contentTargetId, competitors]);

  /** 删除内容记录 */
  const removeContent = useCallback((competitorId: string, contentId: string) => {
    setContentsMap((prev) => {
      const next = {
        ...prev,
        [competitorId]: (prev[competitorId] || []).filter((c) => c.id !== contentId),
      };
      saveJSON(`${CONTENT_STORAGE_PREFIX}${competitorId}`, next[competitorId]);
      return next;
    });
  }, []);

  /** 切换内容爆款标记 */
  const toggleContentHit = useCallback((competitorId: string, contentId: string) => {
    setContentsMap((prev) => {
      const next = {
        ...prev,
        [competitorId]: (prev[competitorId] || []).map((c) =>
          c.id === contentId ? { ...c, isHit: !c.isHit } : c
        ),
      };
      saveJSON(`${CONTENT_STORAGE_PREFIX}${competitorId}`, next[competitorId]);
      return next;
    });
  }, []);

  /** 标记告警为已读 */
  const markAlertRead = useCallback((alertId: string) => {
    setAlerts((prev) => prev.map((a) => (a.id === alertId ? { ...a, read: true } : a)));
  }, []);

  /** 清除已读告警 */
  const clearReadAlerts = useCallback(() => {
    setAlerts((prev) => prev.filter((a) => !a.read));
  }, []);

  // ============================================
  // 派生数据
  // ============================================

  /** 全局统计 */
  const summary = useMemo(() => {
    const totalCompetitors = competitors.length;
    const monitoringCount = competitors.filter((c) => c.monitoring).length;
    const allContents = Object.values(contentsMap).flat();
    const totalContents = allContents.length;
    const totalHits = allContents.filter((c) => c.isHit).length;
    const unreadAlerts = alerts.filter((a) => !a.read).length;
    return { totalCompetitors, monitoringCount, totalContents, totalHits, unreadAlerts };
  }, [competitors, contentsMap, alerts]);

  /** 选中竞品的内容策略分析 */
  const selectedStrategy = useMemo(() => {
    if (!selectedId) return null;
    const contents = contentsMap[selectedId] || [];
    return analyzeContentStrategy(contents);
  }, [selectedId, contentsMap]);

  /** 选中竞品的差异化机会 */
  const selectedOpportunities = useMemo(() => {
    if (!selectedId || !selectedStrategy) return [];
    const comp = competitors.find((c) => c.id === selectedId);
    if (!comp) return [];
    const myStats = extractMyStats(dashboard.marketing.platforms, comp.platform);
    return identifyOpportunities(selectedStrategy, myStats);
  }, [selectedId, selectedStrategy, competitors, dashboard.marketing.platforms]);

  // ============================================
  // 渲染
  // ============================================

  return (
    <motion.div
      initial={{ opacity: 0, y: 8 }}
      animate={{ opacity: 1, y: 0 }}
      transition={{ duration: 0.4 }}
      className="relative rounded-xl border border-white/[0.06] bg-gradient-to-br from-[#131826]/60 to-[#0a0e1a]/60 backdrop-blur-md overflow-hidden"
    >
      {/* 顶部光带 */}
      <div className="absolute top-0 left-0 right-0 h-px bg-gradient-to-r from-transparent via-rose-500/50 to-transparent" />

      {/* Header */}
      <div className="flex items-center justify-between p-4 border-b border-white/[0.04]">
        <div className="flex items-center gap-2.5">
          <div className="w-7 h-7 rounded-md flex items-center justify-center bg-rose-500/15 border border-rose-500/30">
            <Crosshair size={13} className="text-rose-400" />
          </div>
          <div>
            <h3 className="text-[13px] font-semibold text-white tracking-tight">竞品监控与分析</h3>
            <p className="text-[9px] font-mono text-gray-500 uppercase tracking-wider mt-0.5">
              Competitor Monitor · {summary.totalCompetitors} 竞品 · {summary.totalContents} 内容 · {summary.totalHits} 爆款 · {summary.unreadAlerts} 告警
            </p>
          </div>
        </div>
        <div className="flex items-center gap-1">
          <button
            onClick={() => setShowAlertPanel((v) => !v)}
            className="flex items-center gap-1.5 px-2.5 py-1 rounded-md bg-amber-500/15 border border-amber-500/30 hover:bg-amber-500/25 transition-colors relative"
          >
            <Bell size={11} className="text-amber-400" />
            <span className="text-[9px] font-mono text-amber-300 uppercase tracking-wider">告警</span>
            {summary.unreadAlerts > 0 && (
              <span className="absolute -top-1 -right-1 w-3.5 h-3.5 rounded-full bg-red-500 text-[8px] font-mono text-white flex items-center justify-center">
                {summary.unreadAlerts}
              </span>
            )}
          </button>
          <button
            onClick={openAddCompetitor}
            className="flex items-center gap-1.5 px-2.5 py-1 rounded-md bg-cyan-500/15 border border-cyan-500/30 hover:bg-cyan-500/25 transition-colors"
          >
            <Plus size={11} className="text-cyan-400" />
            <span className="text-[9px] font-mono text-cyan-300 uppercase tracking-wider">添加竞品</span>
          </button>
          <button
            onClick={() => setLoading(true)}
            disabled={loading}
            className="flex items-center gap-1.5 px-2.5 py-1 rounded-md bg-rose-500/15 border border-rose-500/30 hover:bg-rose-500/25 transition-colors disabled:opacity-50"
          >
            {loading ? (
              <Loader2 size={11} className="text-rose-400 animate-spin" />
            ) : (
              <RefreshCw size={11} className="text-rose-400" />
            )}
            <span className="text-[9px] font-mono text-rose-300 uppercase tracking-wider">刷新</span>
          </button>
        </div>
      </div>

      {/* 顶部 KPI 概览 */}
      <div className="grid grid-cols-4 gap-2 p-4 border-b border-white/[0.04]">
        <KpiCard icon={Eye} label="监控竞品" value={String(summary.monitoringCount)} color="#F43F5E" />
        <KpiCard icon={FileText} label="内容总数" value={String(summary.totalContents)} color="#38BDF8" />
        <KpiCard icon={Award} label="爆款总数" value={String(summary.totalHits)} color="#FBBF24" />
        <KpiCard icon={AlertCircle} label="未读告警" value={String(summary.unreadAlerts)} color="#A78BFA" />
      </div>

      {/* 竞品列表区域 */}
      <div className="p-4">
        {loading ? (
          <div className="py-10 text-center">
            <Loader2 size={20} className="mx-auto text-rose-400 animate-spin" />
            <p className="text-[10px] font-mono text-gray-600 mt-2">加载竞品数据...</p>
          </div>
        ) : competitors.length === 0 ? (
          // 空状态
          <div className="py-10 text-center">
            <Crosshair size={22} className="mx-auto text-gray-600 mb-2" />
            <p className="text-[11px] font-mono text-gray-500">
              暂无竞品，请添加竞品账号开始监控
            </p>
            <button
              onClick={openAddCompetitor}
              className="mt-3 flex items-center gap-1.5 mx-auto px-3 py-1.5 rounded-md bg-cyan-500/15 border border-cyan-500/30 text-cyan-300 hover:bg-cyan-500/25 transition-colors"
            >
              <Plus size={12} />
              <span className="text-[10px] font-mono uppercase tracking-wider">添加竞品</span>
            </button>
          </div>
        ) : (
          <div className="grid grid-cols-2 gap-2">
            {competitors.map((comp) => {
              const contents = contentsMap[comp.id] || [];
              const strategy = analyzeContentStrategy(contents);
              const isSelected = selectedId === comp.id;
              const platformMeta = PLATFORM_META[comp.platform];
              const myStats = extractMyStats(dashboard.marketing.platforms, comp.platform);
              return (
                <CompetitorCard
                  key={comp.id}
                  competitor={comp}
                  strategy={strategy}
                  platformLabel={platformMeta.label}
                  platformColor={platformMeta.color}
                  myStatsAvailable={myStats.available}
                  myViews={myStats.metrics?.views ?? 0}
                  isSelected={isSelected}
                  onClick={() => setSelectedId(isSelected ? null : comp.id)}
                  onToggleMonitoring={() => toggleMonitoring(comp.id)}
                  onEdit={() => openEditCompetitor(comp)}
                  onDelete={() => removeCompetitor(comp.id)}
                />
              );
            })}
          </div>
        )}
      </div>

      {/* 竞品详情面板 */}
      <AnimatePresence>
        {selectedId && selectedStrategy && (() => {
          const comp = competitors.find((c) => c.id === selectedId);
          if (!comp) return null;
          const contents = contentsMap[selectedId] || [];
          const platformMeta = PLATFORM_META[comp.platform];
          const myStats = extractMyStats(dashboard.marketing.platforms, comp.platform);
          return (
            <motion.div
              initial={{ height: 0, opacity: 0 }}
              animate={{ height: "auto", opacity: 1 }}
              exit={{ height: 0, opacity: 0 }}
              transition={{ duration: 0.2 }}
              className="overflow-hidden border-t border-white/[0.04]"
            >
              <div className="p-4 space-y-3 bg-white/[0.01]">
                {/* 详情 Header */}
                <div className="flex items-center justify-between">
                  <div className="flex items-center gap-2">
                    <BarChart3 size={12} className="text-rose-400" />
                    <span className="text-[11px] font-mono text-gray-300 uppercase tracking-wider">
                      {comp.name} · 详情分析
                    </span>
                    <span
                      className="px-1.5 py-0.5 rounded text-[9px] font-mono"
                      style={{ background: `${platformMeta.color}20`, color: platformMeta.color }}
                    >
                      {platformMeta.label}
                    </span>
                  </div>
                  <button
                    onClick={() => setSelectedId(null)}
                    className="text-gray-500 hover:text-gray-300"
                  >
                    <X size={12} />
                  </button>
                </div>

                {/* 基础信息 */}
                <div className="rounded-lg border border-white/[0.06] bg-white/[0.02] p-4">
                  <div className="text-[9px] font-mono text-gray-500 uppercase tracking-wider mb-2">基础信息</div>
                  <div className="grid grid-cols-3 gap-3 text-[10px] font-mono">
                    <InfoItem label="账号名" value={comp.name} />
                    <InfoItem label="平台" value={platformMeta.label} />
                    <InfoItem label="创建日期" value={comp.createdAt} />
                    <InfoItem label="内容数" value={String(selectedStrategy.total)} />
                    <InfoItem label="爆款数" value={String(selectedStrategy.hitCount)} />
                    <InfoItem label="监控状态" value={comp.monitoring ? "开启" : "暂停"} />
                    {comp.link && (
                      <div className="col-span-3 flex items-center gap-1">
                        <ExternalLink size={9} className="text-gray-500" />
                        <a
                          href={comp.link}
                          target="_blank"
                          rel="noopener noreferrer"
                          className="text-cyan-400 hover:text-cyan-300 truncate"
                        >
                          {comp.link}
                        </a>
                      </div>
                    )}
                    {comp.note && (
                      <div className="col-span-3 text-gray-400">备注：{comp.note}</div>
                    )}
                  </div>
                </div>

                {/* 内容策略分析 */}
                <div className="rounded-lg border border-white/[0.06] bg-white/[0.02] p-4">
                  <div className="flex items-center justify-between mb-2">
                    <div className="flex items-center gap-1.5">
                      <TrendingUp size={11} className="text-cyan-400" />
                      <span className="text-[9px] font-mono text-gray-400 uppercase tracking-wider">
                        内容策略分析
                      </span>
                    </div>
                    <button
                      onClick={() => openContentForm(comp.id)}
                      className="flex items-center gap-1 px-2 py-0.5 rounded text-[9px] font-mono uppercase tracking-wider bg-cyan-500/15 border border-cyan-500/30 text-cyan-300 hover:bg-cyan-500/25 transition-colors"
                    >
                      <Plus size={10} />
                      录入内容
                    </button>
                  </div>
                  {selectedStrategy.total === 0 ? (
                    <p className="text-[10px] font-mono text-gray-600 text-center py-3">
                      暂无内容数据，请点击「录入内容」添加竞品内容记录
                    </p>
                  ) : (
                    <div className="space-y-3">
                      {/* 核心指标 */}
                      <div className="grid grid-cols-4 gap-2">
                        <MetricBox label="日均发布" value={String(selectedStrategy.dailyFrequency)} unit="条/天" color="#38BDF8" />
                        <MetricBox label="周均发布" value={String(selectedStrategy.weeklyFrequency)} unit="条/周" color="#A78BFA" />
                        <MetricBox label="爆款率" value={`${selectedStrategy.hitRate}%`} unit={`${selectedStrategy.hitCount}条`} color="#FBBF24" />
                        <MetricBox label="时间跨度" value={String(selectedStrategy.spanDays)} unit="天" color="#34D399" />
                      </div>
                      {/* 平均表现 + 我方对比 */}
                      <div className="grid grid-cols-3 gap-2">
                        <CompareMetric
                          label="平均阅读"
                          compValue={selectedStrategy.avgViews}
                          myValue={myStats.available ? (myStats.metrics?.views ?? 0) : null}
                          color="#38BDF8"
                        />
                        <CompareMetric
                          label="平均点赞"
                          compValue={selectedStrategy.avgLikes}
                          myValue={myStats.available ? (myStats.metrics?.likes ?? 0) : null}
                          color="#FBBF24"
                        />
                        <CompareMetric
                          label="平均评论"
                          compValue={selectedStrategy.avgComments}
                          myValue={myStats.available ? (myStats.metrics?.comments ?? 0) : null}
                          color="#A78BFA"
                        />
                      </div>
                      {/* 选题方向分布 */}
                      {selectedStrategy.topicDistribution.length > 0 && (
                        <div>
                          <div className="text-[9px] font-mono text-gray-500 uppercase mb-1.5">选题方向分布</div>
                          <div className="space-y-1">
                            {selectedStrategy.topicDistribution.map((t) => (
                              <div key={t.topic} className="flex items-center gap-2">
                                <span className="w-12 text-[10px] font-mono text-gray-300 truncate">
                                  {TOPIC_META[t.topic].label}
                                </span>
                                <div className="flex-1 h-3 rounded bg-white/[0.02] overflow-hidden">
                                  <div
                                    className="h-full rounded"
                                    style={{
                                      width: `${Math.max(5, t.pct)}%`,
                                      background: `linear-gradient(90deg, ${TOPIC_META[t.topic].color}80, ${TOPIC_META[t.topic].color})`,
                                    }}
                                  />
                                </div>
                                <span className="text-[9px] font-mono text-gray-400 w-12 text-right">
                                  {t.count}条 / {t.pct}%
                                </span>
                              </div>
                            ))}
                          </div>
                        </div>
                      )}
                      {/* 内容形式分布 + 发布时段 */}
                      <div className="grid grid-cols-2 gap-3">
                        <div>
                          <div className="text-[9px] font-mono text-gray-500 uppercase mb-1.5">内容形式分布</div>
                          <div className="space-y-1">
                            {selectedStrategy.formDistribution.map((f) => (
                              <div key={f.form} className="flex items-center gap-2">
                                <span className="w-10 text-[10px] font-mono text-gray-300">
                                  {FORM_META[f.form].label}
                                </span>
                                <div className="flex-1 h-2.5 rounded bg-white/[0.02] overflow-hidden">
                                  <div
                                    className="h-full rounded bg-emerald-500/60"
                                    style={{ width: `${Math.max(5, f.pct)}%` }}
                                  />
                                </div>
                                <span className="text-[9px] font-mono text-gray-400 w-10 text-right">
                                  {f.count} / {f.pct}%
                                </span>
                              </div>
                            ))}
                          </div>
                        </div>
                        <div>
                          <div className="text-[9px] font-mono text-gray-500 uppercase mb-1.5">
                            发布时段分布{selectedStrategy.peakHour !== null ? `（主力 ${selectedStrategy.peakHour}:00）` : ""}
                          </div>
                          <div className="flex items-end gap-0.5 h-12">
                            {selectedStrategy.hourDistribution.map((h) => {
                              const maxCount = Math.max(...selectedStrategy.hourDistribution.map((x) => x.count), 1);
                              const heightPct = h.count > 0 ? Math.max(10, Math.round((h.count / maxCount) * 100)) : 2;
                              return (
                                <div
                                  key={h.hour}
                                  className="flex-1 rounded-sm"
                                  style={{
                                    height: `${heightPct}%`,
                                    background: h.hour === selectedStrategy.peakHour ? "#F43F5E" : "#3B82F630",
                                  }}
                                  title={`${h.hour}:00 - ${h.count}条`}
                                />
                              );
                            })}
                          </div>
                          <div className="flex justify-between text-[8px] font-mono text-gray-600 mt-0.5">
                            <span>0</span>
                            <span>12</span>
                            <span>23</span>
                          </div>
                        </div>
                      </div>
                    </div>
                  )}
                </div>

                {/* 爆款拆解列表 */}
                <div className="rounded-lg border border-white/[0.06] bg-white/[0.02] p-4">
                  <div className="flex items-center gap-1.5 mb-2">
                    <Award size={11} className="text-amber-400" />
                    <span className="text-[9px] font-mono text-gray-400 uppercase tracking-wider">
                      爆款拆解（{contents.filter((c) => c.isHit).length} 条）
                    </span>
                  </div>
                  {contents.filter((c) => c.isHit).length === 0 ? (
                    <p className="text-[10px] font-mono text-gray-600 text-center py-2">
                      暂无爆款内容，录入内容后可标记为爆款并添加拆解分析
                    </p>
                  ) : (
                    <div className="space-y-1.5">
                      {contents.filter((c) => c.isHit).map((c) => (
                        <div key={c.id} className="rounded-md border border-amber-500/20 bg-amber-500/[0.04] p-2">
                          <div className="flex items-start justify-between gap-2">
                            <div className="flex-1 min-w-0">
                              <div className="flex items-center gap-1.5">
                                <span className="text-[11px] text-gray-200 truncate">{c.title}</span>
                                <span
                                  className="px-1 rounded text-[8px] font-mono"
                                  style={{ background: `${TOPIC_META[c.topic].color}20`, color: TOPIC_META[c.topic].color }}
                                >
                                  {TOPIC_META[c.topic].label}
                                </span>
                                <span className="px-1 rounded text-[8px] font-mono bg-gray-500/20 text-gray-400">
                                  {FORM_META[c.form].label}
                                </span>
                              </div>
                              <div className="flex items-center gap-2 mt-1 text-[9px] font-mono text-gray-500">
                                <span>{c.publishedAt}</span>
                                <span>阅读 {c.views.toLocaleString()}</span>
                                <span>点赞 {c.likes.toLocaleString()}</span>
                                <span>评论 {c.comments.toLocaleString()}</span>
                              </div>
                              {c.breakdown && (
                                <div className="mt-1 text-[9px] font-mono text-amber-300/80 leading-snug">
                                  拆解：{c.breakdown}
                                </div>
                              )}
                            </div>
                            <button
                              onClick={() => removeContent(comp.id, c.id)}
                              className="text-gray-600 hover:text-red-400 flex-shrink-0"
                            >
                              <Trash2 size={10} />
                            </button>
                          </div>
                        </div>
                      ))}
                    </div>
                  )}
                </div>

                {/* 全部内容列表 */}
                {contents.length > 0 && (
                  <div className="rounded-lg border border-white/[0.06] bg-white/[0.02] p-4">
                    <div className="flex items-center gap-1.5 mb-2">
                      <FileText size={11} className="text-gray-400" />
                      <span className="text-[9px] font-mono text-gray-400 uppercase tracking-wider">
                        全部内容记录（{contents.length} 条）
                      </span>
                    </div>
                    <div className="space-y-1">
                      {contents.map((c) => (
                        <div
                          key={c.id}
                          className="flex items-center gap-2 px-2 py-1 rounded border border-white/[0.04] bg-white/[0.01]"
                        >
                          <button
                            onClick={() => toggleContentHit(comp.id, c.id)}
                            className={`flex-shrink-0 w-4 h-4 rounded border flex items-center justify-center transition-colors ${
                              c.isHit
                                ? "bg-amber-500/30 border-amber-500/50"
                                : "border-gray-600 hover:border-amber-500/40"
                            }`}
                          >
                            {c.isHit && <Award size={9} className="text-amber-400" />}
                          </button>
                          <span className="text-[10px] font-mono text-gray-300 flex-1 truncate">{c.title}</span>
                          <span className="text-[9px] font-mono text-gray-600">{c.publishedAt}</span>
                          <span className="text-[9px] font-mono text-gray-500">{c.views.toLocaleString()}</span>
                          <span
                            className="px-1 rounded text-[8px] font-mono"
                            style={{ background: `${TOPIC_META[c.topic].color}20`, color: TOPIC_META[c.topic].color }}
                          >
                            {TOPIC_META[c.topic].label}
                          </span>
                          <button
                            onClick={() => removeContent(comp.id, c.id)}
                            className="text-gray-600 hover:text-red-400 flex-shrink-0"
                          >
                            <X size={9} />
                          </button>
                        </div>
                      ))}
                    </div>
                  </div>
                )}

                {/* 差异化机会识别 */}
                <div className="rounded-lg border border-white/[0.06] bg-white/[0.02] p-4">
                  <div className="flex items-center gap-1.5 mb-2">
                    <Lightbulb size={11} className="text-yellow-400" />
                    <span className="text-[9px] font-mono text-gray-400 uppercase tracking-wider">
                      差异化机会识别
                    </span>
                    {!myStats.available && (
                      <span className="text-[8px] font-mono text-gray-600">（我方该平台数据未连接）</span>
                    )}
                  </div>
                  {selectedOpportunities.length === 0 ? (
                    <p className="text-[10px] font-mono text-gray-600 text-center py-2">
                      暂无足够数据识别机会，请录入更多竞品内容
                    </p>
                  ) : (
                    <div className="space-y-1.5">
                      {selectedOpportunities.map((op) => (
                        <div
                          key={op.id}
                          className="flex items-start gap-2 px-2 py-1.5 rounded border border-white/[0.04] bg-white/[0.01]"
                          style={{ borderLeft: `2px solid ${op.level === "high" ? "#F43F5E" : op.level === "medium" ? "#FBBF24" : "#6B7280"}` }}
                        >
                          <div className="flex-1 min-w-0">
                            <div className="text-[10px] font-mono text-gray-200">{op.title}</div>
                            <div className="text-[9px] font-mono text-gray-500 mt-0.5 leading-snug">{op.detail}</div>
                          </div>
                          <span
                            className="flex-shrink-0 px-1 rounded text-[8px] font-mono uppercase"
                            style={{
                              color: op.level === "high" ? "#F43F5E" : op.level === "medium" ? "#FBBF24" : "#6B7280",
                              background: op.level === "high" ? "#F43F5E20" : op.level === "medium" ? "#FBBF2420" : "#6B728020",
                            }}
                          >
                            {op.level === "high" ? "高" : op.level === "medium" ? "中" : "低"}
                          </span>
                        </div>
                      ))}
                    </div>
                  )}
                </div>

                {/* 告警规则管理 */}
                <div className="rounded-lg border border-white/[0.06] bg-white/[0.02] p-4">
                  <div className="flex items-center gap-1.5 mb-2">
                    <Bell size={11} className="text-amber-400" />
                    <span className="text-[9px] font-mono text-gray-400 uppercase tracking-wider">
                      告警规则
                    </span>
                  </div>
                  <div className="space-y-1">
                    {comp.alertRules.map((rule) => (
                      <div
                        key={rule.id}
                        className="flex items-center gap-2 px-2 py-1 rounded border border-white/[0.04] bg-white/[0.01]"
                      >
                        <button
                          onClick={() => toggleAlertRule(comp.id, rule.id)}
                          className={`flex-shrink-0 w-7 h-3.5 rounded-full transition-colors relative ${
                            rule.enabled ? "bg-emerald-500/40" : "bg-gray-700"
                          }`}
                        >
                          <span
                            className={`absolute top-0.5 w-2.5 h-2.5 rounded-full bg-white transition-all ${
                              rule.enabled ? "left-4" : "left-0.5"
                            }`}
                          />
                        </button>
                        <span className="text-[10px] font-mono text-gray-300 flex-1">
                          {ALERT_RULE_META[rule.type].label}
                        </span>
                        <span className="text-[9px] font-mono text-gray-600 flex-1 truncate">
                          {ALERT_RULE_META[rule.type].desc}
                        </span>
                        <button
                          onClick={() => removeAlertRule(comp.id, rule.id)}
                          className="text-gray-600 hover:text-red-400 flex-shrink-0"
                        >
                          <X size={9} />
                        </button>
                      </div>
                    ))}
                  </div>
                  {/* 添加告警规则 */}
                  <div className="flex items-center gap-1 mt-2">
                    {ALERT_RULE_OPTIONS
                      .filter((t) => !comp.alertRules.some((r) => r.type === t))
                      .map((t) => (
                        <button
                          key={t}
                          onClick={() => addAlertRule(comp.id, t)}
                          className="flex items-center gap-1 px-1.5 py-0.5 rounded text-[9px] font-mono text-amber-400 hover:text-amber-300 border border-amber-500/20 hover:border-amber-500/40 transition-colors"
                        >
                          <Plus size={9} />
                          {ALERT_RULE_META[t].label}
                        </button>
                      ))}
                    {ALERT_RULE_OPTIONS.every((t) => comp.alertRules.some((r) => r.type === t)) && (
                      <span className="text-[9px] font-mono text-gray-600">已添加全部告警规则</span>
                    )}
                  </div>
                </div>
              </div>
            </motion.div>
          );
        })()}
      </AnimatePresence>

      {/* 告警面板 */}
      <AnimatePresence>
        {showAlertPanel && (
          <motion.div
            initial={{ height: 0, opacity: 0 }}
            animate={{ height: "auto", opacity: 1 }}
            exit={{ height: 0, opacity: 0 }}
            transition={{ duration: 0.2 }}
            className="overflow-hidden border-t border-white/[0.04]"
          >
            <div className="p-4 bg-white/[0.01]">
              <div className="flex items-center justify-between mb-2">
                <div className="flex items-center gap-1.5">
                  <AlertCircle size={11} className="text-amber-400" />
                  <span className="text-[9px] font-mono text-gray-400 uppercase tracking-wider">
                    动态告警（{alerts.length} 条 · 未读 {summary.unreadAlerts}）
                  </span>
                </div>
                {alerts.some((a) => a.read) && (
                  <button
                    onClick={clearReadAlerts}
                    className="text-[9px] font-mono text-gray-500 hover:text-gray-300"
                  >
                    清除已读
                  </button>
                )}
              </div>
              {alerts.length === 0 ? (
                <p className="text-[10px] font-mono text-gray-600 text-center py-3">
                  暂无告警事件，添加告警规则后将在竞品发布爆款时自动触发
                </p>
              ) : (
                <div className="space-y-1">
                  {alerts.map((alert) => (
                    <div
                      key={alert.id}
                      className={`flex items-start gap-2 px-2 py-1.5 rounded border ${
                        alert.read
                          ? "border-white/[0.04] bg-white/[0.01] opacity-60"
                          : "border-amber-500/20 bg-amber-500/[0.04]"
                      }`}
                    >
                      <div
                        className="flex-shrink-0 w-1.5 h-1.5 rounded-full mt-1"
                        style={{ background: alert.read ? "#6B7280" : "#FBBF24" }}
                      />
                      <div className="flex-1 min-w-0">
                        <div className="text-[10px] font-mono text-gray-200">{alert.message}</div>
                        <div className="text-[9px] font-mono text-gray-600 mt-0.5">
                          {alert.triggeredAt} · {ALERT_RULE_META[alert.type].label}
                        </div>
                      </div>
                      {!alert.read && (
                        <button
                          onClick={() => markAlertRead(alert.id)}
                          className="text-[9px] font-mono text-gray-500 hover:text-gray-300 flex-shrink-0"
                        >
                          已读
                        </button>
                      )}
                    </div>
                  ))}
                </div>
              )}
            </div>
          </motion.div>
        )}
      </AnimatePresence>

      {/* 添加/编辑竞品 Modal */}
      <AnimatePresence>
        {showCompetitorForm && (
          <ModalOverlay onClose={() => setShowCompetitorForm(false)}>
            <div className="text-[10px] font-mono text-cyan-400 uppercase tracking-wider mb-3">
              {editingId ? "编辑竞品" : "添加竞品"}
            </div>
            <div className="space-y-2">
              <div>
                <label className="text-[9px] font-mono text-gray-500 uppercase">账号名 *</label>
                <input
                  value={competitorForm.name}
                  onChange={(e) => setCompetitorForm((s) => ({ ...s, name: e.target.value }))}
                  placeholder="如：竞品A"
                  className="w-full mt-1 px-2 py-1.5 rounded-md bg-white/[0.03] border border-white/[0.06] text-[11px] text-gray-200 font-mono focus:outline-none focus:border-cyan-500/40"
                />
              </div>
              <div>
                <label className="text-[9px] font-mono text-gray-500 uppercase">平台</label>
                <select
                  value={competitorForm.platform}
                  onChange={(e) => setCompetitorForm((s) => ({ ...s, platform: e.target.value as MarketingPlatform }))}
                  className="w-full mt-1 px-2 py-1.5 rounded-md bg-white/[0.03] border border-white/[0.06] text-[11px] text-gray-200 font-mono focus:outline-none focus:border-cyan-500/40"
                >
                  {PLATFORM_OPTIONS.map((p) => (
                    <option key={p} value={p}>
                      {PLATFORM_META[p].label}
                    </option>
                  ))}
                </select>
              </div>
              <div>
                <label className="text-[9px] font-mono text-gray-500 uppercase">账号链接（选填）</label>
                <input
                  value={competitorForm.link}
                  onChange={(e) => setCompetitorForm((s) => ({ ...s, link: e.target.value }))}
                  placeholder="https://..."
                  className="w-full mt-1 px-2 py-1.5 rounded-md bg-white/[0.03] border border-white/[0.06] text-[11px] text-gray-200 font-mono focus:outline-none focus:border-cyan-500/40"
                />
              </div>
              <div>
                <label className="text-[9px] font-mono text-gray-500 uppercase">备注（选填）</label>
                <textarea
                  value={competitorForm.note}
                  onChange={(e) => setCompetitorForm((s) => ({ ...s, note: e.target.value }))}
                  placeholder="竞品定位/特征等"
                  rows={2}
                  className="w-full mt-1 px-2 py-1.5 rounded-md bg-white/[0.03] border border-white/[0.06] text-[11px] text-gray-200 font-mono focus:outline-none focus:border-cyan-500/40 resize-none"
                />
              </div>
            </div>
            <div className="flex items-center justify-end gap-2 mt-4">
              <button
                onClick={() => setShowCompetitorForm(false)}
                className="px-2 py-1 rounded text-[9px] font-mono text-gray-400 hover:text-gray-200"
              >
                取消
              </button>
              <button
                onClick={submitCompetitor}
                disabled={!competitorForm.name.trim()}
                className="flex items-center gap-1 px-2 py-1 rounded text-[9px] font-mono uppercase tracking-wider bg-cyan-500/15 border border-cyan-500/30 text-cyan-300 hover:bg-cyan-500/25 transition-colors disabled:opacity-40 disabled:cursor-not-allowed"
              >
                <Plus size={10} />
                {editingId ? "保存" : "添加"}
              </button>
            </div>
          </ModalOverlay>
        )}
      </AnimatePresence>

      {/* 录入内容 Modal */}
      <AnimatePresence>
        {showContentForm && (
          <ModalOverlay onClose={() => setShowContentForm(false)}>
            <div className="text-[10px] font-mono text-cyan-400 uppercase tracking-wider mb-3">
              录入竞品内容
            </div>
            <div className="space-y-2">
              <div>
                <label className="text-[9px] font-mono text-gray-500 uppercase">标题 *</label>
                <input
                  value={contentForm.title}
                  onChange={(e) => setContentForm((s) => ({ ...s, title: e.target.value }))}
                  placeholder="内容标题"
                  className="w-full mt-1 px-2 py-1.5 rounded-md bg-white/[0.03] border border-white/[0.06] text-[11px] text-gray-200 font-mono focus:outline-none focus:border-cyan-500/40"
                />
              </div>
              <div className="grid grid-cols-2 gap-2">
                <div>
                  <label className="text-[9px] font-mono text-gray-500 uppercase">发布日期</label>
                  <input
                    type="date"
                    value={contentForm.publishedAt}
                    onChange={(e) => setContentForm((s) => ({ ...s, publishedAt: e.target.value }))}
                    className="w-full mt-1 px-2 py-1.5 rounded-md bg-white/[0.03] border border-white/[0.06] text-[11px] text-gray-200 font-mono focus:outline-none focus:border-cyan-500/40"
                  />
                </div>
                <div>
                  <label className="text-[9px] font-mono text-gray-500 uppercase">选题方向</label>
                  <select
                    value={contentForm.topic}
                    onChange={(e) => setContentForm((s) => ({ ...s, topic: e.target.value as ContentTopic }))}
                    className="w-full mt-1 px-2 py-1.5 rounded-md bg-white/[0.03] border border-white/[0.06] text-[11px] text-gray-200 font-mono focus:outline-none focus:border-cyan-500/40"
                  >
                    {TOPIC_OPTIONS.map((t) => (
                      <option key={t} value={t}>
                        {TOPIC_META[t].label}
                      </option>
                    ))}
                  </select>
                </div>
              </div>
              <div className="grid grid-cols-3 gap-2">
                <div>
                  <label className="text-[9px] font-mono text-gray-500 uppercase">阅读数</label>
                  <input
                    type="number"
                    value={contentForm.views}
                    onChange={(e) => setContentForm((s) => ({ ...s, views: Number(e.target.value) || 0 }))}
                    className="w-full mt-1 px-2 py-1.5 rounded-md bg-white/[0.03] border border-white/[0.06] text-[11px] text-gray-200 font-mono focus:outline-none focus:border-cyan-500/40"
                  />
                </div>
                <div>
                  <label className="text-[9px] font-mono text-gray-500 uppercase">点赞数</label>
                  <input
                    type="number"
                    value={contentForm.likes}
                    onChange={(e) => setContentForm((s) => ({ ...s, likes: Number(e.target.value) || 0 }))}
                    className="w-full mt-1 px-2 py-1.5 rounded-md bg-white/[0.03] border border-white/[0.06] text-[11px] text-gray-200 font-mono focus:outline-none focus:border-cyan-500/40"
                  />
                </div>
                <div>
                  <label className="text-[9px] font-mono text-gray-500 uppercase">评论数</label>
                  <input
                    type="number"
                    value={contentForm.comments}
                    onChange={(e) => setContentForm((s) => ({ ...s, comments: Number(e.target.value) || 0 }))}
                    className="w-full mt-1 px-2 py-1.5 rounded-md bg-white/[0.03] border border-white/[0.06] text-[11px] text-gray-200 font-mono focus:outline-none focus:border-cyan-500/40"
                  />
                </div>
              </div>
              <div>
                <label className="text-[9px] font-mono text-gray-500 uppercase">内容形式</label>
                <select
                  value={contentForm.form}
                  onChange={(e) => setContentForm((s) => ({ ...s, form: e.target.value as ContentForm }))}
                  className="w-full mt-1 px-2 py-1.5 rounded-md bg-white/[0.03] border border-white/[0.06] text-[11px] text-gray-200 font-mono focus:outline-none focus:border-cyan-500/40"
                >
                  {FORM_OPTIONS.map((f) => (
                    <option key={f} value={f}>
                      {FORM_META[f].label}
                    </option>
                  ))}
                </select>
              </div>
              <div className="flex items-center gap-2">
                <button
                  onClick={() => setContentForm((s) => ({ ...s, isHit: !s.isHit }))}
                  className={`flex items-center gap-1.5 px-2 py-1 rounded text-[9px] font-mono uppercase tracking-wider border transition-colors ${
                    contentForm.isHit
                      ? "bg-amber-500/15 border-amber-500/40 text-amber-300"
                      : "border-white/[0.06] text-gray-500 hover:text-gray-300"
                  }`}
                >
                  <Award size={10} />
                  {contentForm.isHit ? "已标记爆款" : "标记为爆款"}
                </button>
              </div>
              {contentForm.isHit && (
                <div>
                  <label className="text-[9px] font-mono text-gray-500 uppercase">爆款拆解分析</label>
                  <textarea
                    value={contentForm.breakdown}
                    onChange={(e) => setContentForm((s) => ({ ...s, breakdown: e.target.value }))}
                    placeholder="拆解爆款的成功要素：标题钩子/选题角度/结构/情绪点等"
                    rows={3}
                    className="w-full mt-1 px-2 py-1.5 rounded-md bg-white/[0.03] border border-white/[0.06] text-[11px] text-gray-200 font-mono focus:outline-none focus:border-cyan-500/40 resize-none"
                  />
                </div>
              )}
            </div>
            <div className="flex items-center justify-end gap-2 mt-4">
              <button
                onClick={() => setShowContentForm(false)}
                className="px-2 py-1 rounded text-[9px] font-mono text-gray-400 hover:text-gray-200"
              >
                取消
              </button>
              <button
                onClick={submitContent}
                disabled={!contentForm.title.trim()}
                className="flex items-center gap-1 px-2 py-1 rounded text-[9px] font-mono uppercase tracking-wider bg-cyan-500/15 border border-cyan-500/30 text-cyan-300 hover:bg-cyan-500/25 transition-colors disabled:opacity-40 disabled:cursor-not-allowed"
              >
                <Plus size={10} />
                录入
              </button>
            </div>
          </ModalOverlay>
        )}
      </AnimatePresence>
    </motion.div>
  );
}

// ============================================
// 子组件：KPI 小卡片
// ============================================
interface KpiCardProps {
  icon: typeof Eye;
  label: string;
  value: string;
  color: string;
}

function KpiCard({ icon: Icon, label, value, color }: KpiCardProps) {
  return (
    <div
      className="rounded-md border border-white/[0.06] bg-white/[0.02] px-2.5 py-1.5 flex items-center gap-2"
      style={{ borderLeft: `2px solid ${color}` }}
    >
      <Icon size={13} style={{ color }} className="flex-shrink-0" />
      <div className="min-w-0">
        <div className="text-[9px] font-mono text-gray-500 uppercase tracking-wider truncate">{label}</div>
        <div className="text-[13px] font-mono font-semibold truncate" style={{ color }}>
          {value}
        </div>
      </div>
    </div>
  );
}

// ============================================
// 子组件：竞品卡片
// ============================================
interface CompetitorCardProps {
  competitor: Competitor;
  strategy: ContentStrategy;
  platformLabel: string;
  platformColor: string;
  myStatsAvailable: boolean;
  myViews: number;
  isSelected: boolean;
  onClick: () => void;
  onToggleMonitoring: () => void;
  onEdit: () => void;
  onDelete: () => void;
}

function CompetitorCard({
  competitor,
  strategy,
  platformLabel,
  platformColor,
  myStatsAvailable,
  myViews,
  isSelected,
  onClick,
  onToggleMonitoring,
  onEdit,
  onDelete,
}: CompetitorCardProps) {
  // 我方 vs 竞品阅读量对比
  let compareBadge: { text: string; color: string } | null = null;
  if (myStatsAvailable && strategy.avgViews > 0 && myViews > 0) {
    const diff = Math.round(((myViews - strategy.avgViews) / strategy.avgViews) * 100);
    if (Math.abs(diff) >= 10) {
      compareBadge = {
        text: diff > 0 ? `我方 +${diff}%` : `我方 ${diff}%`,
        color: diff > 0 ? "#34D399" : "#F43F5E",
      };
    }
  }

  return (
    <div
      onClick={onClick}
      className={`rounded-lg border bg-white/[0.02] p-3 cursor-pointer transition-colors ${
        isSelected
          ? "border-rose-500/40 bg-rose-500/[0.04]"
          : "border-white/[0.06] hover:border-white/[0.12]"
      }`}
    >
      {/* 卡片头部 */}
      <div className="flex items-start justify-between gap-2">
        <div className="flex-1 min-w-0">
          <div className="flex items-center gap-1.5">
            <span className="text-[12px] text-gray-200 truncate">{competitor.name}</span>
            <span
              className="flex-shrink-0 w-1.5 h-1.5 rounded-full"
              style={{ background: competitor.monitoring ? "#34D399" : "#6B7280" }}
            />
          </div>
          <div className="flex items-center gap-1.5 mt-0.5">
            <span
              className="px-1 rounded text-[8px] font-mono"
              style={{ background: `${platformColor}20`, color: platformColor }}
            >
              {platformLabel}
            </span>
            <span className="text-[9px] font-mono text-gray-600">
              {competitor.monitoring ? "监控中" : "已暂停"}
            </span>
          </div>
        </div>
        <div className="flex items-center gap-1 flex-shrink-0" onClick={(e) => e.stopPropagation()}>
          <button
            onClick={onToggleMonitoring}
            className="text-gray-600 hover:text-emerald-400 p-0.5"
            title={competitor.monitoring ? "暂停监控" : "开启监控"}
          >
            <Eye size={10} />
          </button>
          <button
            onClick={onEdit}
            className="text-gray-600 hover:text-cyan-400 p-0.5"
            title="编辑"
          >
            <Edit2 size={10} />
          </button>
          <button
            onClick={onDelete}
            className="text-gray-600 hover:text-red-400 p-0.5"
            title="删除"
          >
            <Trash2 size={10} />
          </button>
        </div>
      </div>
      {/* 卡片数据 */}
      <div className="grid grid-cols-3 gap-1.5 mt-2.5">
        <div className="text-center">
          <div className="text-[8px] font-mono text-gray-600 uppercase">内容</div>
          <div className="text-[13px] font-mono font-bold text-gray-300">{strategy.total}</div>
        </div>
        <div className="text-center">
          <div className="text-[8px] font-mono text-gray-600 uppercase">爆款</div>
          <div className="text-[13px] font-mono font-bold" style={{ color: strategy.hitCount > 0 ? "#FBBF24" : "#6B7280" }}>
            {strategy.hitCount}
          </div>
        </div>
        <div className="text-center">
          <div className="text-[8px] font-mono text-gray-600 uppercase">日均</div>
          <div className="text-[13px] font-mono font-bold text-cyan-400">{strategy.dailyFrequency}</div>
        </div>
      </div>
      {/* 我方对比 */}
      {compareBadge && (
        <div className="mt-2 flex items-center justify-center">
          <span
            className="px-1.5 py-0.5 rounded text-[8px] font-mono"
            style={{ background: `${compareBadge.color}20`, color: compareBadge.color }}
          >
            {compareBadge.text}
          </span>
        </div>
      )}
      {/* 展开提示 */}
      <div className="mt-2 flex items-center justify-center text-[9px] font-mono text-gray-600">
        {isSelected ? (
          <span className="flex items-center gap-0.5">
            <ChevronDown size={9} /> 收起详情
          </span>
        ) : (
          <span className="flex items-center gap-0.5">
            <ChevronRight size={9} /> 查看详情
          </span>
        )}
      </div>
    </div>
  );
}

// ============================================
// 子组件：信息项
// ============================================
function InfoItem({ label, value }: { label: string; value: string }) {
  return (
    <div>
      <span className="text-gray-600">{label}：</span>
      <span className="text-gray-300">{value}</span>
    </div>
  );
}

// ============================================
// 子组件：指标盒子
// ============================================
function MetricBox({ label, value, unit, color }: { label: string; value: string; unit: string; color: string }) {
  return (
    <div
      className="rounded-md border border-white/[0.04] bg-white/[0.01] px-2 py-1.5 text-center"
      style={{ borderTop: `2px solid ${color}` }}
    >
      <div className="text-[8px] font-mono text-gray-500 uppercase tracking-wider">{label}</div>
      <div className="text-[14px] font-mono font-bold mt-0.5" style={{ color }}>
        {value}
      </div>
      <div className="text-[8px] font-mono text-gray-600">{unit}</div>
    </div>
  );
}

// ============================================
// 子组件：对比指标（竞品 vs 我方）
// ============================================
function CompareMetric({
  label,
  compValue,
  myValue,
  color,
}: {
  label: string;
  compValue: number;
  myValue: number | null;
  color: string;
}) {
  const diff = myValue !== null && compValue > 0 ? Math.round(((myValue - compValue) / compValue) * 100) : null;
  return (
    <div className="rounded-md border border-white/[0.04] bg-white/[0.01] px-2 py-1.5">
      <div className="text-[8px] font-mono text-gray-500 uppercase tracking-wider">{label}</div>
      <div className="flex items-baseline gap-1 mt-0.5">
        <span className="text-[13px] font-mono font-bold" style={{ color }}>
          {compValue.toLocaleString()}
        </span>
        <span className="text-[8px] font-mono text-gray-600">竞品</span>
      </div>
      {myValue !== null ? (
        <div className="flex items-baseline gap-1 mt-0.5">
          <span className="text-[11px] font-mono text-gray-400">{myValue.toLocaleString()}</span>
          <span className="text-[8px] font-mono text-gray-600">我方</span>
          {diff !== null && (
            <span
              className="text-[8px] font-mono"
              style={{ color: diff > 0 ? "#34D399" : diff < 0 ? "#F43F5E" : "#6B7280" }}
            >
              {diff > 0 ? `+${diff}%` : `${diff}%`}
            </span>
          )}
        </div>
      ) : (
        <div className="text-[8px] font-mono text-gray-600 mt-0.5">我方数据未连接</div>
      )}
    </div>
  );
}

// ============================================
// 子组件：Modal 遮罩层
// ============================================
function ModalOverlay({ children, onClose }: { children: React.ReactNode; onClose: () => void }) {
  return (
    <motion.div
      initial={{ opacity: 0 }}
      animate={{ opacity: 1 }}
      exit={{ opacity: 0 }}
      transition={{ duration: 0.15 }}
      className="fixed inset-0 z-50 flex items-center justify-center bg-black/60 backdrop-blur-sm"
      onClick={onClose}
    >
      <motion.div
        initial={{ scale: 0.95, opacity: 0 }}
        animate={{ scale: 1, opacity: 1 }}
        exit={{ scale: 0.95, opacity: 0 }}
        transition={{ duration: 0.15 }}
        onClick={(e) => e.stopPropagation()}
        className="w-[420px] max-w-[90vw] rounded-lg border border-white/[0.08] bg-[#131826]/95 backdrop-blur-md p-4 shadow-2xl"
      >
        {children}
      </motion.div>
    </motion.div>
  );
}
